import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowsClockwise } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSpaceLoggingService, {
  CompanyOption,
  RunLogListItem,
  SpaceWithToggle,
} from '../../service/AdminSpaceLoggingService';

const PAGE_SIZE = 25;

const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const SpaceLoggingPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [spaces, setSpaces] = useState<SpaceWithToggle[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<SpaceWithToggle | null>(
    null,
  );
  const [togglingLogging, setTogglingLogging] = useState(false);
  const [runs, setRuns] = useState<RunLogListItem[]>([]);
  const [runsPage, setRunsPage] = useState(0);
  const [runsTotal, setRunsTotal] = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlRef = useRef({
    orgId: searchParams.get('orgId'),
    spaceId: searchParams.get('spaceId'),
  });
  const restoredCompanyRef = useRef(false);
  const restoredSpaceRef = useRef(false);

  const writeSelectionToUrl = useCallback(
    (company: CompanyOption | null, space: SpaceWithToggle | null) => {
      const params = new URLSearchParams();
      if (company) params.set('orgId', company.id);
      if (space) params.set('spaceId', space.id);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const handleCompanyChange = (next: CompanyOption | null) => {
    setSelectedCompany(next);
    writeSelectionToUrl(next, null);
  };

  const handleSpaceChange = (next: SpaceWithToggle | null) => {
    setSelectedSpace(next);
    setRunsPage(0);
    writeSelectionToUrl(selectedCompany, next);
  };

  useEffect(() => {
    setLoadingCompanies(true);
    AdminSpaceLoggingService.listCompanies()
      .then(rows => {
        setCompanies(
          rows
            .filter(c => !!c?.id)
            .map(c => ({ id: c.id, name: c.name || 'Unnamed org' })),
        );
      })
      .catch(err => {
        console.error('Failed to load organizations', err);
      })
      .finally(() => setLoadingCompanies(false));
  }, []);

  useEffect(() => {
    setSpaces([]);
    setSelectedSpace(null);
    if (!selectedCompany) return;
    AdminSpaceLoggingService.listSpacesForCompany(selectedCompany.id)
      .then(companyWithSpaces => {
        const rows = companyWithSpaces?.spaces ?? [];
        setSpaces(
          rows
            .filter(s => !!s?.id)
            .map(s => ({
              id: s.id,
              name: s.name || 'Unnamed space',
              loggingEnabled: !!s.loggingEnabled,
            })),
        );
      })
      .catch(err => {
        console.error('Failed to load spaces for company', err);
      });
  }, [selectedCompany]);

  const loadRuns = useCallback(async (spaceId: string, page: number) => {
    setLoadingRuns(true);
    try {
      const res = await AdminSpaceLoggingService.listRuns(
        spaceId,
        page + 1,
        PAGE_SIZE,
      );
      setRuns(res.data);
      setRunsTotal(res.total);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedSpace) {
      setRuns([]);
      setRunsTotal(0);
      return;
    }
    loadRuns(selectedSpace.id, runsPage);
  }, [selectedSpace, runsPage, loadRuns]);

  useEffect(() => {
    if (restoredCompanyRef.current) return;
    const orgId = initialUrlRef.current.orgId;
    if (!orgId || companies.length === 0) return;
    const match = companies.find(c => c.id === orgId);
    if (!match) return;
    restoredCompanyRef.current = true;
    setSelectedCompany(match);
  }, [companies]);

  useEffect(() => {
    if (restoredSpaceRef.current) return;
    const spaceId = initialUrlRef.current.spaceId;
    if (!spaceId || spaces.length === 0) return;
    const match = spaces.find(s => s.id === spaceId);
    if (!match) return;
    restoredSpaceRef.current = true;
    setSelectedSpace(match);
  }, [spaces]);

  const handleToggle = async (enabled: boolean) => {
    if (!selectedSpace) return;
    setTogglingLogging(true);
    try {
      const res = await AdminSpaceLoggingService.toggleLogging(
        selectedSpace.id,
        enabled,
      );
      const updated = { ...selectedSpace, loggingEnabled: res.loggingEnabled };
      setSelectedSpace(updated);
      setSpaces(prev => prev.map(s => (s.id === updated.id ? updated : s)));
    } finally {
      setTogglingLogging(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: '#f5f5f5', minHeight: '100vh' }}>
      <Typography variant='h4' sx={{ mb: 3 }}>
        Space Run Logging
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Autocomplete
          sx={{ width: 300 }}
          options={companies}
          loading={loadingCompanies}
          getOptionLabel={o => o.name || o.id}
          value={selectedCompany}
          onChange={(_, v) => handleCompanyChange(v)}
          renderInput={params => (
            <TextField {...params} label='Organization' size='small' />
          )}
        />
        <Autocomplete
          sx={{ width: 300 }}
          options={spaces}
          getOptionLabel={o => o.name || o.id}
          value={selectedSpace}
          onChange={(_, v) => handleSpaceChange(v)}
          disabled={!selectedCompany}
          renderInput={params => (
            <TextField {...params} label='Space' size='small' />
          )}
        />
        {selectedSpace && (
          <FormControlLabel
            control={
              <Switch
                checked={selectedSpace.loggingEnabled}
                onChange={e => handleToggle(e.target.checked)}
                disabled={togglingLogging}
              />
            }
            label='Logging enabled'
          />
        )}
        {selectedSpace && (
          <IconButton
            aria-label='Refresh runs'
            disabled={loadingRuns}
            sx={{ ml: 'auto' }}
            onClick={() => loadRuns(selectedSpace.id, runsPage)}
          >
            <ArrowsClockwise size={24} />
          </IconButton>
        )}
      </Box>
      {selectedSpace && (
        <Box>
          <Typography variant='h6' sx={{ mb: 1 }}>
            Recent runs
          </Typography>
          <TableContainer sx={{ background: '#fff' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Process ID</TableCell>
                  <TableCell>Flow</TableCell>
                  <TableCell>Run type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align='right'>Tokens</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRuns && (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loadingRuns && runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      No runs captured yet.
                    </TableCell>
                  </TableRow>
                )}
                {!loadingRuns &&
                  runs.map(run => (
                    <TableRow
                      key={run.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/space-logging/${run.id}`)}
                    >
                      <TableCell>
                        <code>{run.id.slice(0, 8)}</code>
                      </TableCell>
                      <TableCell>{run.rootFlowName}</TableCell>
                      <TableCell>
                        <Chip size='small' label={run.runType} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={run.status}
                          color={run.status === 'error' ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(run.startedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{formatDuration(run.durationMs)}</TableCell>
                      <TableCell align='right'>{run.totalTokens}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={runsTotal}
            page={runsPage}
            onPageChange={(_, p) => setRunsPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </Box>
      )}
    </Box>
  );
};

export default SpaceLoggingPage;
